import React from 'react';
import Script from 'next/script';
import getConfig from 'next/config';
import { isMobileApp } from '@utils/environment';
import { useGTMConfig } from '@hooks/businessContainer';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_FORCE_COOKIE_CONSENT } from '@monofront/business/constants';
import useIsCheckoutComEnabled from '@hooks/vendor/useIsCheckoutComEnabled';
import useIsApplePayEnabled from '@hooks/vendor/useIsApplePayEnabled';

const {
  publicRuntimeConfig: {
    bingAdsId,
    prismicConfig: { repository },
  },
} = getConfig();

export enum OneTrustCookieCategories {
  NECESSARY = ',C0001,',
  ANALYTICS = ',C0002,',
  FUNCTIONALITY = ',C0003,',
  AD = ',C0004,',
}

/*
    Please refer to the Next documentation to see the different script loading strategies.
    https://nextjs.org/docs/basic-features/script
*/

const Vendors: React.FC = React.memo(() => {
  const isCookieConsentForced = useFeatureIsOn(
    FEATURE_FLAG_FORCE_COOKIE_CONSENT,
  );
  const isCheckoutComEnabled = useIsCheckoutComEnabled();
  const isApplePayEnabled = useIsApplePayEnabled();

  const gtmConfig = useGTMConfig();

  return (
    <>
      {/* Initialize the datalayer  */}
      <Script
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `window.dataLayer = window.dataLayer || []; function gtag() { window.dataLayer.push(arguments); }; ${
            isCookieConsentForced
              ? "gtag('consent', 'default', {ad_storage: 'granted', 'ad_user_data': 'granted', 'ad_personalization': 'granted', analytics_storage: 'granted', functionality_storage: 'granted', security_storage: 'granted', personalization_storage: 'denied', wait_for_update: 1000});"
              : ''
          }  window.dataLayer.push({originalLocation: document.location.protocol + '//' + document.location.hostname + document.location.pathname + document.location.search})`,
        }}
      />

      {!isMobileApp && isCookieConsentForced && (
        <Script
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (window._axcb || (window._axcb = [])).push(function(axeptio) {
		        axeptio.on('cookies:complete', (choices) => {
                  const consent = {
                    ad_storage: 'granted',
                    analytics_storage: 'granted',
                    functionality_storage: 'granted',
                    security_storage: 'granted',
                    // Optional chaining in case it's removed in backoffice
                    personalization_storage: choices.$$googleConsentMode?.ad_personalization
                  };

                  window['gtag']('consent', 'update', consent);

                  window.uetq = window.uetq || [];
                  window.uetq.push('consent', 'update', consent);

                  window.dataLayer.push({
                    event: 'LFConsentUpdated',
                    ...consent,
                    consent_updated: true,
                  });
	             })
		        })

              `,
          }}
        />
      )}

      {/* Checkout.com: Payment Service Provider */}
      {isCheckoutComEnabled && (
        <Script
          strategy="afterInteractive"
          src="https://cdn.checkout.com/js/framesv2.min.js"
        />
      )}

      {/* Set lfABAuth Cookie to false if not present */}
      {!isMobileApp && (
        <Script
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html:
              'void 0===document.cookie.split("; ").find(function(t){return t.startsWith("lfABAuth")})&&(document.cookie="lfABAuth=false; expires=Tue, 01 Jan 2030 00:00:00 UTC; path=/;");',
          }}
        />
      )}

      {/*  Google Tag Manager (GTM) */}
      {!isMobileApp && gtmConfig && (
        <Script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              ${JSON.stringify(
                gtmConfig.src,
              )}+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtmConfig.id}');`,
          }}
          strategy="afterInteractive"
        />
      )}

      {/* Bing (search engine) ads tracking*/}
      {bingAdsId !== '' && !isMobileApp ? (
        <Script
          dangerouslySetInnerHTML={{
            __html: `window.previous_uetq = window.uetq || [];window.uetq = []; window.uetq.push('consent','default',{ad_storage: 'granted', 'ad_user_data': 'granted', 'ad_personalization': 'granted', analytics_storage: 'granted', functionality_storage: 'granted', security_storage: 'granted', personalization_storage: 'denied', wait_for_update: 1000});(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[] ,f=function(){var o={ti:"${bingAdsId}"}; o.q=w[u],w[u]=new UET(o)} ,n=d.createElement(t),n.src=r,n.async=1,n.onload=n .onreadystatechange=function() {var s=this.readyState;s &&s!=="loaded"&& s!=="complete"||(f(),n.onload=n. onreadystatechange=null)},i= d.getElementsByTagName(t)[0],i. parentNode.insertBefore(n,i)})(window,document,"script"," //bat.bing.com/bat.js","uetq"); window.uetq.push(...window.previous_uetq);`,
          }}
          strategy="afterInteractive"
        />
      ) : null}

      {/* Prismic toolbar (useful for Prismic previews) */}
      {!isMobileApp && (
        <Script
          type="text/javascript"
          src={`https://static.cdn.prismic.io/prismic.min.js?new=true&repo=${repository}`}
          strategy="lazyOnload"
        />
      )}
      <Script
        type="text/javascript"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `!function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();`,
        }}
      />
      {/* Apple pay SDK */}
      {isApplePayEnabled && (
        <Script
          crossOrigin="anonymous"
          strategy="afterInteractive"
          src="https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
        />
      )}
    </>
  );
});

export default Vendors;
